import { Alert, Button, Modal, useToast } from '@orbind/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'
import { ListaDaCascata, type NoDaCascata } from './ConcluirEmCadeiaModal.tsx'

/**
 * Pergunta da REABERTURA em cadeia (SPEC-20261006-1355-desmarcar-em-cadeia).
 *
 * Desmarcar um item concluído que tem, embaixo, coisas concluídas — atividades
 * feitas, itens vinculados ou filhos — pergunta o que volta junto, como
 * concluir já perguntava. Três saídas: reabrir só o item (o que desmarcar
 * fazia até aqui), reabrir tudo o que está listado, ou cancelar sem mudar nada.
 *
 * Por que uma janela à parte, e não um `modo` na de concluir: a de concluir
 * tem uma saída e busca a prévia sozinha; esta tem duas saídas que gravam, e
 * recebe a prévia pronta — quem a abre já precisou dela para decidir se havia
 * o que perguntar. Um `modo` viraria um `if` em cada texto, botão e efeito.
 * O que é igual nas duas — a lista — é o mesmo componente (`ListaDaCascata`).
 *
 * `nos === null` é a prévia que falhou: a janela abre mesmo assim, porque
 * reabrir só o item não depende dela, e reabrir tudo sem mostrar o quê não se
 * faz.
 */
export function ReabrirEmCadeiaModal({
  itemId,
  titulo,
  nos,
  onSoEste,
  onFechar,
}: {
  itemId: string
  titulo: string
  nos: readonly NoDaCascata[] | null
  /** "Reabrir só este item": o caminho de sempre, que quem abriu a janela já tem. */
  onSoEste: () => void
  onFechar: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const reabrirTudo = useMutation({
    // resposta de erro vira exceção no próprio cliente (`OrbindApiError`)
    mutationFn: async () =>
      (await api['work-items'][':id'].reabertura.$post({ param: { id: itemId } })).json(),
    onSuccess: (dados) => {
      void queryClient.invalidateQueries({ queryKey: ['work-item'] })
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      if (dados.recusados.length > 0) {
        toast.push({
          tone: 'danger',
          message: `${dados.recusados.length} item(ns) não puderam ser reabertos: ${dados.recusados
            .map((r) => r.codigo ?? r.id)
            .join(', ')}.`,
        })
      } else {
        toast.push({ tone: 'success', message: 'Reaberto, com tudo o que estava embaixo.' })
      }
      onFechar()
    },
    onError: (e) =>
      toast.push({
        tone: 'danger',
        message: e instanceof Error ? e.message : 'Não foi possível reabrir.',
      }),
  })

  // a raiz é o primeiro nó; o resto é o que volta junto
  const juntos = nos ? nos.slice(1) : []

  return (
    <Modal
      open
      onClose={onFechar}
      title="Reabrir em cadeia"
      footer={
        <>
          <Button variant="ghost" onClick={onFechar} disabled={reabrirTudo.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              onSoEste()
              onFechar()
            }}
            disabled={reabrirTudo.isPending}
          >
            Reabrir só este item
          </Button>
          <Button
            variant="primary"
            onClick={() => reabrirTudo.mutate()}
            disabled={nos === null || reabrirTudo.isPending}
          >
            {reabrirTudo.isPending ? 'Reabrindo…' : `Reabrir tudo (${nos?.length ?? 0})`}
          </Button>
        </>
      }
    >
      {nos === null ? (
        <Alert tone="danger" title="Não foi possível montar a lista">
          Dá para reabrir só este item. Para reabrir o que está embaixo, tente de novo.
        </Alert>
      ) : (
        <div className="wi-cascata">
          <p className="wi-cascata__intro">
            <strong>{titulo}</strong> tem {juntos.length}{' '}
            {juntos.length === 1 ? 'coisa concluída' : 'coisas concluídas'} embaixo. Reabrir
            tudo volta também:
          </p>

          <ListaDaCascata nos={nos} linhasFeitas />

          <p className="wi-cascata__nota">
            Os itens voltam para o status inicial e ficam na etapa do fluxo em que estavam.
          </p>
        </div>
      )}
    </Modal>
  )
}
